import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Task #772: top-level React error boundary that catches render-time
 * exceptions (the kind that today produce a blank white screen because no
 * boundary is mounted) and shows the user a friendly fallback page.
 *
 * When the browser Sentry SDK is configured (loaded onto `window.Sentry`,
 * either via the loader script or a future `@sentry/browser` integration),
 * the boundary forwards the exception via `Sentry.captureException(...)` and
 * surfaces the returned event ID with the same `Error ID: <id>` prefix that
 * the API toast layer uses (Task #759 — see `parseErrorMessage` in
 * `lib/queryClient.ts`). When Sentry is not present the boundary still
 * renders the fallback page, just without an ID.
 */

type SentryLike = {
  captureException?: (err: unknown) => string | undefined;
  lastEventId?: () => string | undefined;
};

function getSentry(): SentryLike | null {
  if (typeof window === "undefined") return null;
  const candidate = (window as unknown as { Sentry?: SentryLike }).Sentry;
  if (!candidate || typeof candidate !== "object") return null;
  if (typeof candidate.captureException !== "function") return null;
  return candidate;
}

/**
 * Forward an exception to the browser Sentry SDK if it's configured and
 * return the resulting event ID for display. Defensive: any failure (no SDK,
 * SDK throws, returns garbage) just yields `null` so the boundary still
 * renders its fallback page.
 */
export function captureRenderError(error: unknown): string | null {
  const sentry = getSentry();
  if (!sentry) return null;
  try {
    const id = sentry.captureException?.(error);
    if (typeof id === "string" && id.length > 0) return id;
    const fallback = sentry.lastEventId?.();
    return typeof fallback === "string" && fallback.length > 0 ? fallback : null;
  } catch {
    return null;
  }
}

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  errorId: string | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, errorId: null };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    const errorId = captureRenderError(error);
    this.setState({ errorId });
  }

  private handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    const { errorId } = this.state;

    return (
      <div
        className="min-h-screen w-full flex items-center justify-center bg-gray-50 p-4"
        data-testid="error-boundary-fallback"
      >
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex mb-4 gap-2 items-center">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
            </div>

            {errorId ? (
              <>
                <p className="mt-2 text-sm text-gray-600">
                  We hit an unexpected error rendering this page. Try reloading — if it keeps
                  happening, share the tracking ID below with support so we can dig in.
                </p>
                <p
                  className="mt-4 text-xs font-mono text-gray-700 break-all"
                  data-testid="text-error-id"
                >
                  Error ID: {errorId}
                </p>
              </>
            ) : (
              // Task #1162 — when the browser Sentry SDK isn't configured we
              // never capture an event ID, so the old "share the tracking ID
              // below" copy left users with no actionable next step (and a
              // blank line where the ID should have been). Give them
              // something concrete to send support instead.
              <p className="mt-2 text-sm text-gray-600">
                We hit an unexpected error rendering this page. Try reloading — if it keeps
                happening, contact support with the page URL and the time it occurred so we
                can investigate.
              </p>
            )}

            <div className="mt-6">
              <Button
                type="button"
                onClick={this.handleReload}
                data-testid="button-reload-error-boundary"
              >
                Reload page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

/**
 * Task #2263 — lightweight inline error boundary for individual cards /
 * sections inside a page. When a child throws during render, this renders
 * a compact amber notice ("We couldn't load this section — try refreshing")
 * instead of bubbling to the top-level ErrorBoundary and blanking the whole
 * page. Forwards the exception to Sentry so the event is still tracked.
 */
type InlineSectionBoundaryState = {
  hasError: boolean;
};

export class InlineSectionBoundary extends Component<ErrorBoundaryProps, InlineSectionBoundaryState> {
  state: InlineSectionBoundaryState = { hasError: false };

  static getDerivedStateFromError(): InlineSectionBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    captureRenderError(error);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="border border-amber-200 rounded-lg p-4 mt-4 bg-amber-50 flex items-start gap-3"
        data-testid="inline-section-boundary-fallback"
      >
        <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-900">We couldn't load this section</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Try refreshing the page. If the problem persists, contact support.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
